<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <!-- Statistics Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" variant="tonal" color="primary">
          <v-card-text class="pa-4 text-center">
            <v-icon size="32" color="primary" class="mb-2">mdi-cash-multiple</v-icon>
            <div class="text-h6 font-weight-bold">{{ stats.total || 0 }}</div>
            <div class="text-caption">Tổng yêu cầu</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" variant="tonal" color="warning">
          <v-card-text class="pa-4 text-center">
            <v-icon size="32" color="warning" class="mb-2">mdi-clock-outline</v-icon>
            <div class="text-h6 font-weight-bold">{{ stats.pending || 0 }}</div>
            <div class="text-caption">Chờ xử lý</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" variant="tonal" color="success">
          <v-card-text class="pa-4 text-center">
            <v-icon size="32" color="success" class="mb-2">mdi-check-circle</v-icon>
            <div class="text-h6 font-weight-bold">{{ stats.completed || 0 }}</div>
            <div class="text-caption">Đã hoàn thành</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" variant="tonal" color="info">
          <v-card-text class="pa-4 text-center">
            <v-icon size="32" color="info" class="mb-2">mdi-currency-usd</v-icon>
            <div class="text-h6 font-weight-bold">{{ formatCurrency(stats.total_amount || 0) }}</div>
            <div class="text-caption">Tổng tiền</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title class="text-h6 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-bank-transfer</v-icon>
          Danh sách yêu cầu rút tiền
        </div>
      </v-card-title>

      <!-- Filters -->
      <v-card-text>
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="search"
              label="Tìm kiếm"
              placeholder="Tên, STK, ngân hàng..."
              @input="onSearchInput"
              clearable
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filterStatus"
              label="Trạng thái"
              :items="statusOptions"
              @update:modelValue="onFilterChange"
              clearable
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="sortBy"
              label="Sắp xếp theo"
              :items="sortByOptions"
              @update:modelValue="onSortChange"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="sortOrder"
              label="Thứ tự"
              :items="sortOrderOptions"
              @update:modelValue="onSortChange"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>

        <!-- Desktop Table View -->
        <div v-if="!$vuetify.display.mobile" class="desktop-view">
          <v-table class="elevation-1">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Số điện thoại</th>
                <th>Số tiền</th>
                <th>Chủ tài khoản</th>
                <th>Số tài khoản</th>
                <th>Ngân hàng</th>
                <th>Ngày tạo</th>
                <th class="text-center">Trạng thái</th>
                <th class="text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="withdrawal in allWithdrawals" :key="withdrawal.id">
                <td>{{ withdrawal.id }}</td>
                <td>{{ withdrawal.user_email || 'N/A' }}</td>
                <td>{{ withdrawal.user_phone || 'N/A' }}</td>
                <td class="font-weight-bold text-success">{{ formatCurrency(withdrawal.amount) }}</td>
                <td>{{ withdrawal.bank_holder }}</td>
                <td>{{ withdrawal.bank_account }}</td>
                <td>
                  <v-chip color="primary" size="small" variant="outlined">
                    {{ withdrawal.bank_name }}
                  </v-chip>
                </td>
                <td>{{ formatDate(withdrawal.created_at) }}</td>
                <td class="text-center">
                  <v-chip :color="withdrawal.status === 1 ? 'success' : 'warning'" size="small">
                    {{ withdrawal.status === 1 ? 'Đã hoàn thành' : 'Chờ xử lý' }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <div class="d-flex justify-center gap-2">
                    <v-btn
                      v-if="withdrawal.status === 0"
                      icon
                      size="small"
                      color="success"
                      @click="approveWithdrawal(withdrawal)"
                      title="Duyệt rút tiền"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      color="primary"
                      @click="openEditDialog(withdrawal)"
                      title="Chỉnh sửa"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      color="error"
                      @click="deleteWithdrawal(withdrawal)"
                      title="Xóa"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-pagination
            v-model="page"
            :length="totalPages"
            @update:modelValue="onPageChange"
            class="custom-pagination"
          />
        </div>

        <!-- Mobile Card View -->
        <div v-else class="mobile-view">
          <div class="mobile-withdrawals-container" ref="withdrawalsContainer">
            <v-card 
              v-for="withdrawal in allWithdrawals" 
              :key="withdrawal.id" 
              class="mb-3 withdrawal-card"
              elevation="2">
              <v-card-text>
                <div class="d-flex justify-space-between align-center mb-2">
                  <v-chip color="primary" size="small" variant="outlined">
                    ID: {{ withdrawal.id }}
                  </v-chip>
                  <v-chip :color="withdrawal.status === 1 ? 'success' : 'warning'" size="small">
                    {{ withdrawal.status === 1 ? 'Đã hoàn thành' : 'Chờ xử lý' }}
                  </v-chip>
                </div>
                
                <div class="withdrawal-info">
                  <div class="info-row">
                    <v-icon size="16" color="primary" class="mr-2">mdi-email</v-icon>
                    <strong>Email:</strong>
                    <span class="ml-2">{{ withdrawal.user_email || 'N/A' }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="info" class="mr-2">mdi-phone</v-icon>
                    <strong>SĐT:</strong>
                    <span class="ml-2">{{ withdrawal.user_phone || 'N/A' }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="success" class="mr-2">mdi-cash</v-icon>
                    <strong>Số tiền:</strong>
                    <span class="ml-2 font-weight-bold text-success">{{ formatCurrency(withdrawal.amount) }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="info" class="mr-2">mdi-account-card</v-icon>
                    <strong>Chủ TK:</strong>
                    <span class="ml-2">{{ withdrawal.bank_holder }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="warning" class="mr-2">mdi-credit-card</v-icon>
                    <strong>Số TK:</strong>
                    <span class="ml-2">{{ withdrawal.bank_account }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="purple" class="mr-2">mdi-bank</v-icon>
                    <strong>Ngân hàng:</strong>
                    <span class="ml-2">{{ withdrawal.bank_name }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="grey" class="mr-2">mdi-calendar</v-icon>
                    <strong>Ngày tạo:</strong>
                    <span class="ml-2">{{ formatDate(withdrawal.created_at) }}</span>
                  </div>
                </div>
                
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    v-if="withdrawal.status === 0"
                    color="success"
                    size="small"
                    @click="approveWithdrawal(withdrawal)"
                    block
                  >
                    <v-icon class="mr-2">mdi-check</v-icon>
                    Duyệt
                  </v-btn>
                  <v-btn
                    color="primary"
                    size="small"
                    @click="openEditDialog(withdrawal)"
                    block
                  >
                    <v-icon class="mr-2">mdi-pencil</v-icon>
                    Sửa
                  </v-btn>
                  <v-btn
                    color="error"
                    size="small"
                    @click="deleteWithdrawal(withdrawal)"
                    block
                  >
                    <v-icon class="mr-2">mdi-delete</v-icon>
                    Xóa
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- Loading indicator for infinite scroll -->
          <div v-if="loadingMore" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">Đang tải thêm...</div>
          </div>
          
          <!-- End of list indicator -->
          <div v-if="hasReachedEnd && allWithdrawals.length > 0" class="text-center py-4 text-grey">
            <v-icon>mdi-check-circle</v-icon>
            <div>Đã hiển thị tất cả yêu cầu rút tiền</div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Chỉnh sửa yêu cầu rút tiền
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editForm.amount"
                label="Số tiền"
                type="number"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editForm.bank_holder"
                label="Chủ tài khoản"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editForm.bank_account"
                label="Số tài khoản"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editForm.bank_name"
                label="Ngân hàng"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="editForm.status"
                label="Trạng thái"
                :items="[
                  { title: 'Chờ xử lý', value: 0 },
                  { title: 'Đã hoàn thành', value: 1 }
                ]"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="editDialog = false">Hủy</v-btn>
          <v-btn color="primary" @click="updateWithdrawal">Lưu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/http';

export default {
  name: 'Withdrawal',
  data() {
    return {
      withdrawals: [],
      allWithdrawals: [], // For infinite scroll
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
      search: '',
      filterStatus: null,
      sortBy: 'created_at',
      sortOrder: 'DESC',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      pageLoading: false,
      loadingMore: false,
      hasReachedEnd: false,
      currentMobilePage: 1,
      stats: {
        total: 0,
        pending: 0,
        completed: 0,
        total_amount: 0,
        pending_amount: 0,
        completed_amount: 0
      },
      editDialog: false,
      editForm: {
        id: null,
        amount: '',
        bank_holder: '',
        bank_account: '',
        bank_name: '',
        status: 0
      },
      statusOptions: [
        { title: 'Tất cả', value: null },
        { title: 'Chờ xử lý', value: 0 },
        { title: 'Đã hoàn thành', value: 1 }
      ],
      sortByOptions: [
        { title: 'Ngày tạo', value: 'created_at' },
        { title: 'ID', value: 'id' },
        { title: 'Số tiền', value: 'amount' },
        { title: 'Trạng thái', value: 'status' }
      ],
      sortOrderOptions: [
        { title: 'Giảm dần', value: 'DESC' },
        { title: 'Tăng dần', value: 'ASC' }
      ]
    }
  },
  methods: {
    async fetchWithdrawals(page = this.page, limit = this.limit, showPageLoading = false, append = false) {
      if (showPageLoading) {
        this.pageLoading = true;
      }
      if (append) {
        this.loadingMore = true;
      }
      
      try {
        const params = {
          page,
          limit,
          sort_by: this.sortBy,
          sort_order: this.sortOrder
        };
        
        if (this.search) {
          params.search = this.search;
        }
        
        if (this.filterStatus !== null && this.filterStatus !== undefined) {
          params.status = this.filterStatus;
        }
        
        const res = await axios.get('/admin/withdrawal', { params });
        
        if (append) {
          this.allWithdrawals = [...this.allWithdrawals, ...res.data.data];
          this.hasReachedEnd = res.data.data.length < limit || page >= res.data.pagination.totalPages;
        } else {
          this.withdrawals = res.data.data;
          this.allWithdrawals = res.data.data;
        }
        
        this.total = res.data.pagination.total;
        this.totalPages = res.data.pagination.totalPages;
        this.page = res.data.pagination.page;
      } catch (e) {
        console.error('Error fetching withdrawals:', e);
        this.snackbarText = 'Lấy danh sách yêu cầu rút tiền thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        if (showPageLoading) {
          this.pageLoading = false;
        }
        if (append) {
          this.loadingMore = false;
        }
      }
    },

    async fetchStats() {
      try {
        const res = await axios.get('/admin/withdrawal/stats/overview');
        this.stats = res.data.data;
      } catch (e) {
        console.error('Error fetching stats:', e);
      }
    },

    async loadMoreWithdrawals() {
      if (this.loadingMore || this.hasReachedEnd) return;
      
      this.currentMobilePage++;
      await this.fetchWithdrawals(this.currentMobilePage, this.limit, false, true);
    },

    resetMobileData() {
      this.allWithdrawals = [];
      this.currentMobilePage = 1;
      this.hasReachedEnd = false;
    },
    
    onSearchInput() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => {
        this.page = 1;
        this.resetMobileData();
        this.fetchWithdrawals(1, this.limit);
      }, 400);
    },

    onFilterChange() {
      this.page = 1;
      this.resetMobileData();
      this.fetchWithdrawals(1, this.limit);
    },

    onSortChange() {
      this.page = 1;
      this.resetMobileData();
      this.fetchWithdrawals(1, this.limit);
    },
    
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchWithdrawals(newPage, this.limit);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('vi-VN');
    },
    
    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0₫';
      return amount.toLocaleString('vi-VN') + '₫';
    },

    async approveWithdrawal(withdrawal) {
      if (!confirm('Bạn có chắc chắn muốn duyệt yêu cầu rút tiền này?')) {
        return;
      }

      try {
        await axios.put(`/admin/withdrawal/${withdrawal.id}/status`, { status: 1 });
        withdrawal.status = 1;
        this.snackbarText = 'Duyệt yêu cầu rút tiền thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        this.fetchStats(); // Refresh stats
      } catch (e) {
        console.error('Error approving withdrawal:', e);
        this.snackbarText = e.response?.data?.message || 'Duyệt yêu cầu rút tiền thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    openEditDialog(withdrawal) {
      this.editForm = {
        id: withdrawal.id,
        amount: withdrawal.amount,
        bank_holder: withdrawal.bank_holder,
        bank_account: withdrawal.bank_account,
        bank_name: withdrawal.bank_name,
        status: withdrawal.status
      };
      this.editDialog = true;
    },

    async updateWithdrawal() {
      try {
        const updateData = {
          amount: this.editForm.amount,
          bank_holder: this.editForm.bank_holder,
          bank_account: this.editForm.bank_account,
          bank_name: this.editForm.bank_name,
          status: this.editForm.status
        };

        await axios.put(`/admin/withdrawal/${this.editForm.id}`, updateData);
        
        this.editDialog = false;
        this.snackbarText = 'Cập nhật yêu cầu rút tiền thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        
        // Refresh data
        this.fetchWithdrawals(this.page, this.limit);
        this.fetchStats();
      } catch (e) {
        console.error('Error updating withdrawal:', e);
        this.snackbarText = e.response?.data?.message || 'Cập nhật yêu cầu rút tiền thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    async deleteWithdrawal(withdrawal) {
      if (!confirm('Bạn có chắc chắn muốn xóa yêu cầu rút tiền này?')) {
        return;
      }

      try {
        await axios.delete(`/admin/withdrawal/${withdrawal.id}`);
        this.snackbarText = 'Xóa yêu cầu rút tiền thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        
        // Refresh data
        this.fetchWithdrawals(this.page, this.limit);
        this.fetchStats();
      } catch (e) {
        console.error('Error deleting withdrawal:', e);
        this.snackbarText = e.response?.data?.message || 'Xóa yêu cầu rút tiền thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    setupInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      
      this.$nextTick(() => {
        if (this.$refs.withdrawalsContainer) {
          this.observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && this.$vuetify.display.mobile) {
              this.loadMoreWithdrawals();
            }
          }, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1
          });
          
          const sentinel = document.createElement('div');
          sentinel.style.height = '1px';
          sentinel.style.background = 'transparent';
          this.$refs.withdrawalsContainer?.appendChild(sentinel);
          this.observer.observe(sentinel);
        }
      });
    }
  },
  
  async mounted() {
    this.pageLoading = true;
    try {
      await Promise.all([
        this.fetchWithdrawals(this.page, this.limit),
        this.fetchStats()
      ]);
    } finally {
      this.pageLoading = false;
    }
    this.setupInfiniteScroll();
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>

<style scoped>
.search-input {
  max-width: 320px;
  margin-bottom: 0;
}

.custom-pagination {
  margin-top: 20px;
}

.gap-2 {
  gap: 8px;
}

/* Summary Cards */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px !important;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.summary-card .text-h6 {
  font-size: 1.5rem !important;
  line-height: 1.2 !important;
}

.summary-card .text-caption {
  font-size: 0.85rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-withdrawals-container {
  padding: 8px 0;
}

.withdrawal-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.withdrawal-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.withdrawal-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 80px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.text-grey {
  color: #999;
  font-style: italic;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-withdrawals-container {
    padding: 4px 0;
  }
  
  .withdrawal-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 75px;
    font-size: 13px;
  }

  .summary-card .text-h6 {
    font-size: 1.25rem !important;
  }
  
  .summary-card .text-caption {
    font-size: 0.75rem !important;
  }
}
</style>
